import Image from 'next/image'
import React from 'react'

import clsx from 'clsx'
import { FormattedMessage } from 'react-intl'

import { DEFAULT_CURRENCY } from '@/lib/constants'
import { Device } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

import DeviceHostingPrice from '@/components/DeviceHostingPrice'
import { DevicePerformance } from '@/components/DevicePerformance'
import Icon from '@/components/Icon'
import Tooltip from '@/components/Tooltip'

import { ShopCardPaymentBlock } from './ShopCardPaymentBlock'

const ShopCard = ({ device }: { device: Device }) => {
  const { miner, currency: deviceCurrency, price } = device
  const { location, cover } = miner

  const currency = deviceCurrency || DEFAULT_CURRENCY

  return (
    <div className="space-y-4 tablet:space-y-6">
      <div
        className={clsx(
          'relative flex flex-col gap-3 rounded-xl border border-slate-300 p-4 pb-5 tablet:p-5 desktop:p-6'
        )}
      >
        <div className="flex gap-5 max-tablet:flex-col tablet:gap-6">
          <DeviceImage cover={cover} />
          <div className="flex flex-auto flex-col">
            <DevicePerformance
              device={device}
              angleComponent={
                <h3 className="text-2xl font-medium leading-xs max-tablet:pt-1 tablet:ml-auto tablet:text-3xl tablet:leading-3xl">
                  {formatCurrency(price, currency)}
                </h3>
              }
            />

            <div className="flex flex-col gap-1 pt-5 tablet:flex-row tablet:gap-1.5 tablet:pt-3 desktop:pb-3 desktop:pt-0">
              <div className="flex text-x-sm leading-x-sm tablet:w-1/2 desktop:mt-auto">
                <FormattedMessage
                  {...{
                    id: 'device.miner.location',
                    defaultMessage: 'Установлен'
                  }}
                />
                :<span className="ml-0.5 font-medium">{location.name}</span>
              </div>
              <DeviceHostingPrice
                className="tablet:w-1/2 tablet:text-right"
                {...{ ...device }}
              />
            </div>
            <ShopCardPaymentBlock
              {...{ device, className: 'max-desktop:hidden' }}
            />
          </div>
        </div>
        <ShopCardPaymentBlock {...{ device, className: 'desktop:hidden' }} />
      </div>
    </div>
  )
}

export default ShopCard

const DeviceImage = ({
  cover,
  className
}: {
  cover: any
  className?: string
}) => {
  return (
    <div
      className={clsx(
        'flex w-full flex-none flex-col items-center max-tablet:gap-5 tablet:w-[176px] desktop:w-[216px] desktop:justify-between',
        className
      )}
    >
      <div className="max-h-[185px] w-fit overflow-hidden rounded-lg tablet:h-[133px] desktop:h-[175px]">
        <Image
          className="block h-full w-auto max-w-full object-contain"
          alt="Shop card"
          height={195}
          priority
          src={cover?.thumbURL || cover?.url || '/images/miner.png'}
          width={260}
        />
      </div>
      <div className="flex items-center gap-1.5">
        <p className="text-[11px] font-medium leading-[1.27] tracking-[-0.24px]">
          <FormattedMessage
            {...{
              id: 'tooltip.guarantee.title',
              defaultMessage: 'Пожизненная гарантия'
            }}
          />
        </p>
        <Tooltip
          className="max-w-[228px]"
          contentComponent={
            <FormattedMessage
              {...{
                id: 'tooltip.guarantee.text',
                defaultMessage:
                  'Пожизненная гарантия означает, что Asterbit осуществляет весь ремонт, техническое обслуживание, закупает запасные части и расходные материалы для всех размещенных устройств.'
              }}
            />
          }
        >
          <Icon
            className="inline h-4 w-4 flex-none cursor-pointer text-secondary-500"
            name="info-italic-16"
          />
        </Tooltip>
      </div>
    </div>
  )
}
